import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CalendarDays, LayoutList, Loader2, Clock, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  Scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  Confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Completed: "bg-slate-100 text-slate-700 border-slate-200",
  Cancelled: "bg-red-100 text-red-800 border-red-200",
};

export default function Appointments() {
  const [appts, setAppts] = useState(null);
  const [team, setTeam] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [view, setView] = useState("list");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", start: "", end: "", service_type: "", address: "", notes: "", assigned_to: "unassigned", customer_id: "none", status: "Scheduled" });

  const load = useCallback(() => { api.get("/appointments").then(({ data }) => setAppts(data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/business/team").then(({ data }) => setTeam(data)).catch(() => {});
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title || !form.start) { toast.error("Title and start time are required"); return; }
    setLoading(true);
    try {
      await api.post("/appointments", {
        ...form,
        assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to,
        customer_id: form.customer_id === "none" ? null : form.customer_id,
      });
      toast.success("Appointment created");
      setDialogOpen(false);
      setForm({ title: "", start: "", end: "", service_type: "", address: "", notes: "", assigned_to: "unassigned", customer_id: "none", status: "Scheduled" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const cancelAppt = async (id) => { await api.delete(`/appointments/${id}`); toast.success("Appointment cancelled"); load(); };

  const teamName = (id) => team.find((m) => m.id === id)?.name || "Unassigned";

  // calendar helpers
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const apptsOn = (d) => (appts || []).filter((a) => {
    const dt = new Date(a.start);
    return dt.getFullYear() === month.getFullYear() && dt.getMonth() === month.getMonth() && dt.getDate() === d;
  });

  const upcoming = (appts || []).filter((a) => a.status !== "Cancelled");

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Appointments</h1>
          <p className="text-slate-500 mt-1">Schedule and dispatch jobs.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <button onClick={() => setView("list")} data-testid="appt-view-list" className={`p-1.5 rounded-md ${view === "list" ? "bg-blue-600 text-white" : "text-slate-500"}`}><LayoutList className="h-4 w-4" /></button>
            <button onClick={() => setView("calendar")} data-testid="appt-view-calendar" className={`p-1.5 rounded-md ${view === "calendar" ? "bg-blue-600 text-white" : "text-slate-500"}`}><CalendarDays className="h-4 w-4" /></button>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-appt-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New appointment</Button>
        </div>
      </div>

      {appts === null ? <Skeleton className="h-96 rounded-xl" /> : view === "list" ? (
        upcoming.length === 0 ? (
          <Card className="p-12 text-center border-slate-200" data-testid="appts-empty">
            <CalendarDays className="h-12 w-12 text-slate-300 mx-auto" />
            <h3 className="mt-4 font-heading font-semibold text-lg">No appointments scheduled</h3>
            <p className="text-slate-500 mt-1">Create your first appointment to get started.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {upcoming.map((a) => (
              <Card key={a.id} className="p-4 border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`appt-row-${a.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{a.title}</span>
                    <Badge className={`border ${STATUS_STYLES[a.status]}`}>{a.status}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(a.start).toLocaleString()}</span>
                    {a.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{a.address}</span>}
                    <span>{teamName(a.assigned_to)}</span>
                  </div>
                </div>
                {a.status !== "Cancelled" && <Button variant="outline" size="sm" onClick={() => cancelAppt(a.id)} data-testid={`cancel-appt-${a.id}`} className="text-red-600">Cancel</Button>}
              </Card>
            ))}
          </div>
        )
      ) : (
        <Card className="p-4 sm:p-6 border-slate-200" data-testid="appt-calendar">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">{month.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-400 mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => (
              <div key={i} className={`min-h-[72px] rounded-lg p-1 ${d ? "bg-slate-50 border border-slate-100" : ""}`}>
                {d && <div className="text-xs text-slate-400 mb-1">{d}</div>}
                {d && apptsOn(d).slice(0, 3).map((a) => (
                  <div key={a.id} className="text-[10px] bg-blue-600 text-white rounded px-1 py-0.5 mb-0.5 truncate" title={a.title}>{a.title}</div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="appt-dialog">
          <DialogHeader><DialogTitle>New appointment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={set("title")} data-testid="appt-title" className="mt-1" placeholder="Water heater install" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start *</Label><Input type="datetime-local" value={form.start} onChange={set("start")} data-testid="appt-start" className="mt-1" /></div>
              <div><Label>End</Label><Input type="datetime-local" value={form.end} onChange={set("end")} data-testid="appt-end" className="mt-1" /></div>
            </div>
            <div><Label>Service type</Label><Input value={form.service_type} onChange={set("service_type")} data-testid="appt-service" className="mt-1" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="appt-address" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assign to</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger className="mt-1" data-testid="appt-assigned"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="appt-customer"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} data-testid="appt-notes" className="mt-1" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={loading} data-testid="appt-save-btn" className="bg-blue-600 hover:bg-blue-700">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
